import type { Campaign, DisplayCreative } from '@feathr/blackbox';
import { CallToActionTypes } from '@feathr/blackbox';

export const ZOOM_PRESETS = [25, 50, 75, 100, 150, 200] as const;
export const DEFAULT_MIN_ZOOM = 25;
export const DEFAULT_MAX_ZOOM = 200;
export const DEFAULT_ZOOM_STEP = 25;
export const DEFAULT_ZOOM = 100;

/**
 * Clamps a zoom value between a min and max.
 * @param zoom - The zoom value to clamp.
 * @param min - The minimum zoom value.
 * @param max - The maximum zoom value.
 * @returns The clamped zoom value.
 */
export function clampZoom(zoom: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, zoom));
}

/**
 * Calculates the zoom level needed to fit content within a viewport.
 * @param contentWidth - Natural width of the content.
 * @param contentHeight - Natural height of the content.
 * @param viewportWidth - Width of the viewport.
 * @param viewportHeight - Height of the viewport.
 * @param padding - Padding in pixels to subtract from viewport dimensions. Defaults to 32.
 * @returns The zoom percentage that fits the content without scrolling.
 */
export function calculateZoomToFit(
  contentWidth: number,
  contentHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  padding: number = 32,
): number {
  if (contentWidth <= 0 || contentHeight <= 0 || viewportWidth <= 0 || viewportHeight <= 0) {
    return DEFAULT_ZOOM;
  }

  const availableWidth = viewportWidth - padding;
  const availableHeight = viewportHeight - padding;

  if (availableWidth <= 0 || availableHeight <= 0) {
    return DEFAULT_ZOOM;
  }

  const scaleX = availableWidth / contentWidth;
  const scaleY = availableHeight / contentHeight;
  const scale = Math.min(scaleX, scaleY);

  return Math.round(scale * 100);
}

/**
 * Formats an ad tag by replacing click tracking placeholders with the provided click URL.
 *
 * @param adtag - The raw ad tag HTML containing placeholders
 * @param clickURL - The URL to use for click tracking
 * @returns The formatted ad tag HTML with placeholders replaced
 *
 * @example
 * ```ts
 * const formatted = formatAdTag('<a href="@FEATHR_CLICK@">Click</a>', 'https://example.com');
 * // Returns: '<a href="https://example.com">Click</a>'
 * ```
 */
export function formatAdTag(adtag: string, clickURL: string): string {
  return adtag
    .replace(/@FEATHR_CLICK@/g, clickURL)
    .replace(/@FEATHR_CLICK_ESC@/g, encodeURIComponent(clickURL))
    .replace(/@FEATHR_TRACK@/g, '');
}

/**
 * Builds a test click URL for ad preview click-through tracking.
 *
 * @param creative - The creative being previewed
 * @param campaign - The campaign containing destination URL and account info
 * @returns The formatted test click URL, or undefined if no destination URL is available
 *
 * @example
 * ```ts
 * const url = buildTestClickUrl(creative, campaign);
 * // Returns: 'https://polo.feathr.co/analytics/crumb?rdr=...' or undefined
 * ```
 */
export function buildTestClickUrl(
  creative: DisplayCreative,
  campaign: Campaign,
): string | undefined {
  const destinationURL = creative.get('destination_url') || campaign.get('destination_url');
  if (!destinationURL) {
    return undefined;
  }
  const useOriginalDestination = creative.get('use_original_destination') ? 1 : 0;
  const queryStr = `?rdr=${encodeURIComponent(destinationURL)}&preview=1&a_id=${campaign.get(
    'account',
  )}&use_original_destination=${useOriginalDestination}&preview_name=${encodeURIComponent(creative.get('name') ?? '')}&ordr=`;
  return `${POLO_URL}analytics/crumb${queryStr}`;
}

/**
 * Gets the CTA display label for a given call_to_action_type enum value.
 *
 * @param ctaType - The call_to_action_type value from the creative
 * @returns The human-readable label, or undefined if not found
 *
 * @example
 * ```ts
 * getCtaLabel('LEARN_MORE'); // Returns: 'Learn more'
 * getCtaLabel(undefined);    // Returns: undefined
 * ```
 */
export function getCtaLabel(ctaType: string | undefined): string | undefined {
  if (!ctaType) {
    return undefined;
  }
  return CallToActionTypes.find((cta) => cta.id === ctaType)?.name;
}
